import styled from 'styled-components';
import { media } from 'helpers/breakpoints';
import { ButtonLink } from 'components/Toolkit/Button/ButtonLink';
import { Link } from 'components/Toolkit/Button/Link';
import { ValuationLookupComponent } from 'components/HubPages/components/ValuationLookupComponent/ValuationLookupComponent';
import type { HistoryCheckItems, Image, Text } from 'api/types/hubpageApiTypes';
import { GreenlightRegistrationLookup } from 'components/HubPages/components/GreenlightHistoryCheck/GreenlightRegistrationLookup';

export interface HeroContentProps {
  heroTitle: string;
  ctaText: string;
  ctaLink: string;
  infoText?: string;
  infoTextLink?: string;
  heroSponsoredText?: string;
  heroSponsoredImage?: Image;
  historyCheckItemsTitle?: Text;
  historyCheckItems?: HistoryCheckItems[];
  titleImage?: Image;
  showValuationComponent?: boolean;
  showRegLookup?: boolean;
  valuationLookupDestinationPage?: string;
  cdnUrl: string;
  baseUrl: string;
}

const Container = styled.div<{
  showValuationComponent?: boolean;
  showRegLookup?: boolean;
}>`
  ${({ showValuationComponent, showRegLookup }) =>
    !(showRegLookup || showValuationComponent) &&
    `
      height: 360px;
      ${media.large}{
        height: 400px;
      }
    }`};
`;

const Wrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  flex-direction: column;
`;

const HeroH1 = styled.h1`
  ${({ theme }) => theme.fontSize.H24};
  color: ${({ theme }) => theme.colors.WHITE};
  margin-bottom: ${({ theme }) => theme.spacing.M24};
  max-width: 288px;
  text-align: center;
  align-self: center;
  margin-top: ${({ theme }) => theme.spacing.L48};

  ${media.large} {
    ${({ theme }) => theme.fontSize.H32};
    max-width: 480px;
    margin-top: ${({ theme }) => theme.spacing.XL72};
  }
`;

const SButtonLink = styled(ButtonLink)`
  width: 240px;
  margin: 0 auto;
`;

const SponsoredLink = styled.div`
  justify-content: flex-end;
  align-self: flex-end;
  width: 100%;
`;

const LinkWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  ${media.large} {
    align-items: end;
    justify-content: end;
  }
`;

const SLink = styled(Link)`
  ${({ theme }) => theme.fontSize.B12};
  color: ${({ theme }) => theme.colors.WHITE};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  padding: ${({ theme }) => `${theme.spacing.S4} ${theme.spacing.S8}`};
  background: rgba(0, 0, 0, 0.48);
  box-shadow: 0px 1px 4px rgba(51, 51, 51, 0.24);
  border-radius: 4px;
  text-transform: capitalize;
  margin-top: ${({ theme }) => theme.spacing.L56};
  margin-bottom: 24px;
`;

function HeroContent({
  heroTitle,
  ctaText,
  ctaLink,
  infoText,
  infoTextLink,
  heroSponsoredText,
  heroSponsoredImage,
  titleImage,
  showValuationComponent = false,
  showRegLookup = false,
  valuationLookupDestinationPage,
  baseUrl,
  historyCheckItemsTitle,
  historyCheckItems,
}: HeroContentProps) {
  const showValuation = !!(
    valuationLookupDestinationPage && showValuationComponent
  );
  return (
    <Container
      showValuationComponent={showValuation}
      showRegLookup={showRegLookup}
    >
      <Wrapper>
        {showRegLookup && (
          <ContentWrapper>
            <GreenlightRegistrationLookup
              heroTitle={heroTitle}
              ctaText={ctaText}
              titleImage={titleImage}
              historyCheckItemsTitle={historyCheckItemsTitle}
              historyCheckItems={historyCheckItems}
            />
          </ContentWrapper>
        )}
        {showValuation && (
          <ContentWrapper>
            <ValuationLookupComponent
              ctaText={heroTitle}
              valuationLookupDestinationPage={valuationLookupDestinationPage}
              hero_cta={ctaText}
              baseUrl={baseUrl}
              heroSponsoredText={heroSponsoredText}
              heroSponsoredImage={heroSponsoredImage}
            />
          </ContentWrapper>
        )}
        {!showRegLookup && !showValuation && (
          <>
            <ContentWrapper>
              <HeroH1>{heroTitle}</HeroH1>
              {ctaText && (
                <SButtonLink href={ctaLink} target="_blank">
                  {ctaText}
                </SButtonLink>
              )}
            </ContentWrapper>
            <SponsoredLink>
              {infoText && infoTextLink && (
                <div className="row set-width-100">
                  <div className="columns small-12">
                    <LinkWrapper>
                      <SLink href={infoTextLink} target="_blank">
                        {infoText}
                      </SLink>
                    </LinkWrapper>
                  </div>
                </div>
              )}
            </SponsoredLink>
          </>
        )}
      </Wrapper>
    </Container>
  );
}

export { HeroContent };
